import axios from 'axios';

const inferredApiUrl =
  typeof window !== 'undefined' && window.location?.hostname
    ? `${window.location.protocol}//${window.location.hostname}:8000`
    : 'http://localhost:8000';
const API_URL =
  process.env.REACT_APP_API_URL || inferredApiUrl;

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TENANT_STORAGE_KEYS = ['current_tenant_id', 'tenant_id', 'selected_tenant_id'];

const clearStoredAuthSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_type');
  TENANT_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const getStoredTenantId = () => {
  for (const key of TENANT_STORAGE_KEYS) {
    const value = localStorage.getItem(key);
    if (value && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const isAuthRoute = (url = '') => {
  const normalized = String(url || '').toLowerCase();
  return normalized.includes('/api/auth/login') || normalized.includes('/api/auth/refresh');
};

// Adiciona o header Authorization se existir token salvo
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const tenantId = getStoredTenantId();
  if (tenantId && !isAuthRoute(config.url)) {
    config.headers['X-Tenant-ID'] = tenantId;
  }

  return config;
});

// Atualiza token ou força logout em respostas 401
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se não for 401 ou se for retry já processado, apenas rejeita
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return Promise.reject(error);
    }

    // Evita múltiplos refresh em paralelo
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token });
      const newToken = refreshResponse.data?.access_token;
      const newRefresh = refreshResponse.data?.refresh_token;

      if (newToken) {
        localStorage.setItem('access_token', newToken);
        if (newRefresh) {
          localStorage.setItem('refresh_token', newRefresh);
        }
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      processQueue(new Error('Falha ao atualizar token'));
      return Promise.reject(error);
    } catch (refreshError) {
      processQueue(refreshError);
      clearStoredAuthSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Tipo Unidade Services
export const tipoUnidadeService = {
  list: () => api.get('/api/tipo-unidades/'),
  get: (id) => api.get(`/api/tipo-unidades/${id}`),
  create: (data) => api.post('/api/tipo-unidades/', data),
  update: (id, data) => api.put(`/api/tipo-unidades/${id}`, data),
  delete: (id) => api.delete(`/api/tipo-unidades/${id}`),
};

// Unidade Services
export const unidadeService = {
  list: (params) => api.get('/api/unidades/', { params }),
  get: (id) => api.get(`/api/unidades/${id}`),
  create: (data) => api.post('/api/unidades/', data),
  update: (id, data) => api.put(`/api/unidades/${id}`, data),
  delete: (id) => api.delete(`/api/unidades/${id}`),
};

// Modalidade Services
export const modalidadeService = {
  list: () => api.get('/api/modalidades/'),
  get: (id) => api.get(`/api/modalidades/${id}`),
  create: (data) => api.post('/api/modalidades/', data),
  update: (id, data) => api.put(`/api/modalidades/${id}`, data),
  delete: (id) => api.delete(`/api/modalidades/${id}`),
};

// Serie Services
export const serieService = {
  list: (params) => api.get('/api/series/', { params }),
  get: (id) => api.get(`/api/series/${id}`),
  create: (data) => api.post('/api/series/', data),
  update: (id, data) => api.put(`/api/series/${id}`, data),
  delete: (id) => api.delete(`/api/series/${id}`),
};

// Tipo Contrato Services
export const tipoContratoService = {
  list: () => api.get('/api/tipo-contratos/'),
  get: (id) => api.get(`/api/tipo-contratos/${id}`),
  create: (data) => api.post('/api/tipo-contratos/', data),
  update: (id, data) => api.put(`/api/tipo-contratos/${id}`, data),
  delete: (id) => api.delete(`/api/tipo-contratos/${id}`),
};

// Equipe Services
export const equipeService = {
  list: (params) => api.get('/api/equipes/', { params }),
  get: (id) => api.get(`/api/equipes/${id}`),
  create: (data) => api.post('/api/equipes/', data),
  update: (id, data) => api.put(`/api/equipes/${id}`, data),
  delete: (id) => api.delete(`/api/equipes/${id}`),
  vincularUsuario: (id, data) => api.post(`/api/equipes/${id}/vincular-usuario`, data),
  desvincularUsuario: (id) => api.post(`/api/equipes/${id}/desvincular-usuario`),
};

// Turma Services
export const turmaService = {
  list: (params) => api.get('/api/turmas/', { params }),
  get: (id) => api.get(`/api/turmas/${id}`),
  create: (data) => api.post('/api/turmas/', data),
  update: (id, data) => api.put(`/api/turmas/${id}`, data),
  delete: (id) => api.delete(`/api/turmas/${id}`),
  recalcularAulas: (id, onlyMissing = true) => api.post(`/api/turmas/${id}/recalcular-aulas`, null, { params: { only_missing: onlyMissing } }),
};

// Aluno Services
export const alunoService = {
  list: (params) => api.get('/api/alunos/', { params }),
  get: (id) => api.get(`/api/alunos/${id}`),
  create: (data) => api.post('/api/alunos/', data),
  update: (id, data) => api.put(`/api/alunos/${id}`, data),
  delete: (id) => api.delete(`/api/alunos/${id}`),
};

// Usuario Services
export const usuarioService = {
  list: (params) => api.get('/api/usuarios/', { params }),
  get: (id) => api.get(`/api/usuarios/${id}`),
  create: (data) => api.post('/api/usuarios/', data),
  update: (id, data) => api.put(`/api/usuarios/${id}`, data),
  delete: (id) => api.delete(`/api/usuarios/${id}`),
  buscar: (termo, limit = 20) => api.get('/api/usuarios/buscar', { params: { termo, limit } }),
  getMeuPerfil: () => api.get('/api/usuarios/me/perfil'),
  alterarMeuEmail: (data) => api.put('/api/usuarios/me/email', data),
  alterarMinhaSenha: (data) => api.put('/api/usuarios/me/senha', data),
};

// Responsavel Services
export const responsavelService = {
  list: (params) => api.get('/api/responsaveis/', { params }),
  get: (id) => api.get(`/api/responsaveis/${id}`),
  create: (data) => api.post('/api/responsaveis/', data),
  update: (id, data) => api.put(`/api/responsaveis/${id}`, data),
  delete: (id) => api.delete(`/api/responsaveis/${id}`),
  buscarAlunosPorCpf: (cpf) => api.get(`/api/responsaveis/buscar-alunos-por-cpf/${cpf}`),
  vincularAluno: (responsavelId, data) => api.post(`/api/responsaveis/${responsavelId}/vincular-aluno`, data),
  vincularUsuario: (responsavelId, data) => api.post(`/api/responsaveis/${responsavelId}/vincular-usuario`, data),
  desvincularAluno: (responsavelId, alunoId) => api.patch(`/api/responsaveis/${responsavelId}/desvincular-aluno/${alunoId}`),
  listarAlunos: (responsavelId) => api.get(`/api/responsaveis/${responsavelId}/alunos`),
};

// Matricula Services
export const matriculaService = {
  list: (params) => api.get('/api/matriculas/', { params }),
  listAdmin: (params) => api.get('/api/matriculas/admin-list', { params }),
  get: (id) => api.get(`/api/matriculas/${id}`),
  getDetalhes: (id) => api.get(`/api/matriculas/${id}/detalhes`),
  create: (data) => api.post('/api/matriculas/', data),
  update: (id, data) => api.put(`/api/matriculas/${id}`, data),
  ativar: (id, data) => api.post(`/api/matriculas/${id}/ativar`, data),
  cancelar: (id) => api.post(`/api/matriculas/${id}/cancelar`),
  trocarVigencia: (id, data) => api.post(`/api/matriculas/${id}/trocar-vigencia`, data),
  alterarVencimentoFuturo: (id, data) => api.post(`/api/matriculas/${id}/alterar-vencimento-futuro`, data),
  corrigirVigenciaInicial: (id, data) => api.post(`/api/matriculas/${id}/corrigir-vigencia-inicial`, data),
  delete: (id) => api.delete(`/api/matriculas/${id}`),
  listParcelas: (id) => api.get(`/api/matriculas/${id}/parcelas`),
  sincronizarStatusParcelas: (id) => api.post(`/api/matriculas/${id}/parcelas/sincronizar-status`),
  recalcularParcelas: (id) => api.post(`/api/matriculas/${id}/recalcular-parcelas`),
  deleteParcelas: (id) => api.delete(`/api/matriculas/${id}/parcelas`),
  deleteParcelasBulk: (id) => api.delete(`/api/matriculas/${id}/parcelas`),
};

export const contratoService = {
  list: (params) => api.get('/api/contratos', { params }),
  get: (id) => api.get(`/api/contratos/${id}`),
  create: (data) => api.post('/api/contratos', data),
  update: (id, data) => api.put(`/api/contratos/${id}`, data),
  delete: (id) => api.delete(`/api/contratos/${id}`),
  previewPorMatricula: (matriculaId) => api.get(`/api/contratos/gerar-por-matricula/${matriculaId}/preview`),
  gerarPorMatricula: (matriculaId, data) => api.post(`/api/contratos/gerar-por-matricula/${matriculaId}`, data),
  enviarParaAssinatura: (id) => api.post(`/api/contratos/${id}/enviar-para-assinatura`),
  sincronizarStatus: (id) => api.put(`/api/contratos/${id}/sincronizar-status`),
  cancelar: (id) => api.put(`/api/contratos/${id}/cancelar`),
};

export const parcelaService = {
  list: (params) => api.get('/api/parcelas', { params }),
  delete: (id) => api.delete(`/api/parcelas/${id}`),
  getCobrancaCompartilhamento: (id) => api.get(`/api/parcelas/${id}/cobranca-compartilhamento`),
  enviarCobrancaEmail: (id) => api.post(`/api/parcelas/${id}/cobranca-compartilhamento/enviar-email`),
};

// Aulas Dadas Services
export const aulaDadaService = {
  list: (params) => api.get('/api/aulas-dadas/', { params }),
  get: (id) => api.get(`/api/aulas-dadas/${id}`),
  create: (data) => api.post('/api/aulas-dadas/', data),
  update: (id, data) => api.put(`/api/aulas-dadas/${id}`, data),
  delete: (id) => api.delete(`/api/aulas-dadas/${id}`),
  deletePendentesTurma: (turmaId) => api.delete(`/api/aulas-dadas/turma/${turmaId}/pendentes`),
  validar: (id, data) => api.post(`/api/aulas-dadas/${id}/validar`, data),
  ajustarHorario: (id, data) => api.post(`/api/aulas-dadas/${id}/ajustar-horario`, data),
  reverter: (id) => api.post(`/api/aulas-dadas/${id}/reverter`),
  relatorioMensal: (params) => api.get('/api/aulas-dadas/relatorio/mensal', { params }),
  relatorioAulasDadas: (params) => api.get('/api/aulas-dadas/relatorio/aulas', { params }),
};

// Fornecedor Services
export const fornecedorService = {
  list: (params) => api.get('/api/fornecedores/', { params }),
  get: (id) => api.get(`/api/fornecedores/${id}`),
  create: (data) => api.post('/api/fornecedores/', data),
  update: (id, data) => api.put(`/api/fornecedores/${id}`, data),
  delete: (id) => api.delete(`/api/fornecedores/${id}`),
};

// Categoria Despesa Services
export const categoriaDespesaService = {
  list: (params) => api.get('/api/categorias-despesa/', { params }),
  get: (id) => api.get(`/api/categorias-despesa/${id}`),
  create: (data) => api.post('/api/categorias-despesa/', data),
  update: (id, data) => api.put(`/api/categorias-despesa/${id}`, data),
  delete: (id) => api.delete(`/api/categorias-despesa/${id}`),
};

// Despesa Services
export const despesaService = {
  list: (params) => api.get('/api/despesas/', { params }),
  get: (id) => api.get(`/api/despesas/${id}`),
  create: (data) => api.post('/api/despesas/', data),
  update: (id, data) => api.put(`/api/despesas/${id}`, data),
  delete: (id) => api.delete(`/api/despesas/${id}`),
  darBaixa: (id) => api.post(`/api/despesas/${id}/dar-baixa`),
  reverterBaixa: (id) => api.post(`/api/despesas/${id}/reverter-baixa`),
};

// Relatórios Services
export const relatorioService = {
  repasseGlobal: (params) => api.get('/api/relatorios/repasse-global', { params }),
  matriculasAtivas: (params) => api.get('/api/relatorios/matriculas-ativas', { params }),
  getRelatorioAlunosAtivos: (params) => api.get('/api/relatorios/alunos-ativos', { params }),
  getRelatorioTurmasAbertas: (params) => api.get('/api/relatorios/turmas-abertas', { params }),
  getRelatorioMatriculasAtivas: (params) => api.get('/api/relatorios/matriculas-ativas-operacional', { params }),
  getRelatorioProfessoresSimples: (params) => api.get('/api/relatorios/professores-simples', { params }),
  contabeis: (params) => api.get('/api/relatorios/contabeis', { params }),
};

// Receita Services
export const receitaService = {
  list: (params) => api.get('/api/receitas/', { params }),
  get: (id) => api.get(`/api/receitas/${id}`),
  create: (data) => api.post('/api/receitas/', data),
  gerarParcelas: (matriculaId) => api.post(`/api/receitas/gerar-parcelas/${matriculaId}`),
  darBaixa: (id, data) => api.post(`/api/receitas/${id}/dar-baixa`, data),
  reverterBaixa: (id) => api.post(`/api/receitas/${id}/reverter-baixa`),
  update: (id, data) => api.put(`/api/receitas/${id}`, data),
  delete: (id) => api.delete(`/api/receitas/${id}`),
  forecastResumo: (params) => api.get('/api/receitas/forecast/resumo', { params }),
};

// Fluxo de Caixa Services
export const fluxoCaixaService = {
  get: (params) => api.get('/api/fluxo-caixa/', { params }),
  dashboard: (params) => api.get('/api/fluxo-caixa/dashboard', { params }),
};

// Portal do Professor Services
export const professorPortalService = {
  listarEncontros: ({ data } = {}) => api.get('/api/professor/encontros', {
    params: data ? { data } : {},
  }),
  obterChamada: (encontroId) => api.get(`/api/professor/encontros/${encontroId}/chamada`),
  salvarChamada: (encontroId, payload) => api.put(`/api/professor/encontros/${encontroId}/chamada`, payload),
};

export const adminPresencasService = {
  listarChamadas: ({
    dataInicio,
    dataFim,
    unidadeId,
    modalidadeId,
    turmaId,
    equipeId,
    alunoId,
    statusChamada,
    page,
    pageSize,
    ordenarPor,
    direcao,
  } = {}) => api.get('/api/admin/presencas/chamadas', {
    params: {
      data_inicio: dataInicio ?? undefined,
      data_fim: dataFim ?? undefined,
      unidade_id: unidadeId ?? undefined,
      modalidade_id: modalidadeId ?? undefined,
      turma_id: turmaId ?? undefined,
      equipe_id: equipeId ?? undefined,
      aluno_id: alunoId ?? undefined,
      status_chamada: statusChamada ?? undefined,
      page: page ?? undefined,
      page_size: pageSize ?? undefined,
      ordenar_por: ordenarPor ?? undefined,
      direcao: direcao ?? undefined,
    },
  }),
  obterDetalheChamada: (encontroId) => api.get(`/api/admin/presencas/chamadas/${encontroId}`),
};

// Folha de Pagamento Services
export const folhaPagamentoService = {
  list: (params) => api.get('/api/folha-pagamento/', { params }),
  get: (id) => api.get(`/api/folha-pagamento/${id}`),
  detalhamento: (id) => api.get(`/api/folha-pagamento/${id}/detalhamento`),
  itens: (id) => api.get(`/api/folha-pagamento/${id}/itens`),
  retroativosPendentes: (id) => api.get(`/api/folha-pagamento/${id}/retroativos-pendentes`),
  gerar: (data) => api.post('/api/folha-pagamento/gerar', data),
  gerarAjustesRetroativos: (id, data) => api.post(`/api/folha-pagamento/${id}/gerar-ajustes-retroativos`, data),
  preview: (mesReferencia, idEquipe = null) => api.get(
    `/api/folha-pagamento/preview/${mesReferencia}`,
    idEquipe ? { params: { id_equipe: idEquipe } } : undefined
  ),
  atualizarRascunho: (id) => api.post(`/api/folha-pagamento/${id}/atualizar-rascunho`),
  fechar: (id) => api.post(`/api/folha-pagamento/${id}/fechar`),
  darBaixa: (id) => api.post(`/api/folha-pagamento/${id}/dar-baixa`),
  reverterBaixa: (id) => api.post(`/api/folha-pagamento/${id}/reverter-baixa`),
  update: (id, data) => api.put(`/api/folha-pagamento/${id}`, data),
  delete: (id) => api.delete(`/api/folha-pagamento/${id}`),
};

export const receiptIssuerSettingsService = {
  get: () => api.get('/api/configuracoes/emitente-padrao'),
  update: (payload) => api.put('/api/configuracoes/emitente-padrao', payload),
};

// Perfil Services
export const perfilService = {
  list: () => api.get('/api/perfis/'),
  get: (id) => api.get(`/api/perfis/${id}`),
  create: (data) => api.post('/api/perfis/', data),
  update: (id, data) => api.put(`/api/perfis/${id}`, data),
  delete: (id) => api.delete(`/api/perfis/${id}`),
  listMenuItens: () => api.get('/api/perfis/menu-itens'),
};

export const financialIntegrationsService = {
  listProviders: () => api.get('/api/integrations/financial/providers'),
  getConfig: () => api.get('/api/integrations/financial/config'),
  updateTenantDefault: (payload) => api.put('/api/integrations/financial/config/default', payload),
  testConnection: (payload) => api.post('/api/integrations/financial/test-connection', payload),
};

export const platformIntegrationsService = {
  listProviders: (params) => api.get('/api/platform-admin/integrations/providers', { params }),
  updateProviderStatus: (providerId, payload) =>
    api.patch(`/api/platform-admin/integrations/providers/${providerId}/status`, payload),
};

export const platformSaasBillingCatalogService = {
  createPlan: (payload) => api.post('/api/platform-admin/saas-billing/plans', payload),
  listPlans: (params) => api.get('/api/platform-admin/saas-billing/plans', { params }),
  getPlanById: (planId) => api.get(`/api/platform-admin/saas-billing/plans/${planId}`),
  getPlanByCode: (code) => api.get(`/api/platform-admin/saas-billing/plans/by-code/${code}`),
  patchPlanStatus: (planId, payload) => api.patch(`/api/platform-admin/saas-billing/plans/${planId}/status`, payload),
  patchPlanIsPublic: (planId, payload) => api.patch(`/api/platform-admin/saas-billing/plans/${planId}/is-public`, payload),

  createFeature: (payload) => api.post('/api/platform-admin/saas-billing/features', payload),
  listFeatures: (params) => api.get('/api/platform-admin/saas-billing/features', { params }),
  getFeatureById: (featureId) => api.get(`/api/platform-admin/saas-billing/features/${featureId}`),
  getFeatureByCode: (code) => api.get(`/api/platform-admin/saas-billing/features/by-code/${code}`),
  patchFeatureStatus: (featureId, payload) => api.patch(`/api/platform-admin/saas-billing/features/${featureId}/status`, payload),

  createPlanEntitlement: (planId, payload) =>
    api.post(`/api/platform-admin/saas-billing/plans/${planId}/entitlements`, payload),
  updatePlanEntitlement: (planId, featureId, payload) =>
    api.put(`/api/platform-admin/saas-billing/plans/${planId}/entitlements/${featureId}`, payload),
  deletePlanEntitlement: (planId, entitlementId) =>
    api.delete(`/api/platform-admin/saas-billing/plans/${planId}/entitlements/${entitlementId}`),
  listPlanEntitlements: (planId) =>
    api.get(`/api/platform-admin/saas-billing/plans/${planId}/entitlements`),
};

export const platformSaasBillingSubscriptionService = {
  getTenantCurrentSubscription: (tenantId) =>
    api.get(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription/current`),
  listTenantSubscriptions: (tenantId) =>
    api.get(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscriptions`),
  createTenantSubscription: (tenantId, payload) =>
    api.post(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription`, payload),
  changeTenantSubscriptionPlan: (tenantId, payload) =>
    api.post(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription/change-plan`, payload),
  changeTenantSubscriptionStatus: (tenantId, payload) =>
    api.patch(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription/status`, payload),
  updateTenantSubscriptionDates: (tenantId, payload) =>
    api.patch(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription/dates`, payload),
  cancelTenantSubscription: (tenantId) =>
    api.post(`/api/platform-admin/saas-billing/tenants/${tenantId}/subscription/cancel`),
  getTenantEffectiveEntitlements: (tenantId) =>
    api.get(`/api/platform-admin/saas-billing/tenants/${tenantId}/entitlements`),
  getTenantSaasUsage: (tenantId) =>
    api.get(`/api/platform-admin/saas-billing/tenants/${tenantId}/usage`),
  getTenantSaasLifecycle: (tenantId) =>
    api.get(`/api/platform-admin/saas-billing/tenants/${tenantId}/lifecycle`),
};

export const platformSubscriptionChangeRequestsService = {
  list: (params) => api.get('/api/platform-admin/saas/subscription-change-requests', { params }),
  approve: (requestId, payload) =>
    api.post(`/api/platform-admin/saas/subscription-change-requests/${requestId}/approve`, payload),
  reject: (requestId, payload) =>
    api.post(`/api/platform-admin/saas/subscription-change-requests/${requestId}/reject`, payload),
  preparePayment: (requestId) =>
    api.post(`/api/platform-admin/saas/subscription-change-requests/${requestId}/prepare-payment`, {}),
};

export const platformSaasTimelineService = {
  listTenantTimeline: (tenantId, params) =>
    api.get(`/api/platform-admin/saas/tenants/${tenantId}/timeline`, { params }),
};

export const platformOperationalEventsService = {
  list: (params) => api.get('/api/platform-admin/saas/operational-events', { params }),
};

export const platformOperationalHealthService = {
  get: (params) => api.get('/api/platform-admin/saas/operational-health', { params }),
};

export const platformOperationalAlertsService = {
  list: (params) => api.get('/api/platform-admin/saas/alerts', { params }),
  evaluate: (payload) => api.post('/api/platform-admin/saas/alerts/evaluate', payload || {}),
  acknowledge: (alertId, payload) => api.post(`/api/platform-admin/saas/alerts/${alertId}/acknowledge`, payload || {}),
  resolve: (alertId, payload) => api.post(`/api/platform-admin/saas/alerts/${alertId}/resolve`, payload || {}),
};

export const platformFeatureFlagsService = {
  list: (params) => api.get('/api/platform-admin/saas/feature-flags', { params }),
  createOrUpdate: (payload) => api.post('/api/platform-admin/saas/feature-flags', payload),
  setEnabled: (flagId, payload) => api.patch(`/api/platform-admin/saas/feature-flags/${flagId}/enabled`, payload),
  resolve: (params) => api.get('/api/platform-admin/saas/feature-flags/resolve', { params }),
};

export const platformReadinessService = {
  getReadinessReport: () => api.get('/api/platform-admin/saas/readiness-report'),
};

export const platformIamService = {
  getIamMePermissions: () => api.get('/api/platform-admin/iam/me/permissions'),
  getIamRoles: () => api.get('/api/platform-admin/iam/roles'),
  getIamPermissions: () => api.get('/api/platform-admin/iam/permissions'),
  getIamUsers: () => api.get('/api/platform-admin/iam/users'),
  getIamCoverage: () => api.get('/api/platform-admin/iam/coverage'),
  getIamUserEffectivePermissions: (userId) =>
    api.get(`/api/platform-admin/iam/users/${encodeURIComponent(String(userId || '').trim())}/effective-permissions`),
};

export const accountSaasService = {
  getCurrentSubscription: () => api.get('/api/account/saas/subscription/current'),
  getLifecycle: () => api.get('/api/account/saas/lifecycle'),
  getEntitlements: () => api.get('/api/account/saas/entitlements'),
};

export const saasSelfService = {
  getSubscription: () => api.get('/api/saas/self-service/subscription'),
  listUpgradePlans: () => api.get('/api/saas/self-service/subscription/upgrade-plans'),
  listDowngradePlans: () => api.get('/api/saas/self-service/subscription/downgrade-plans'),
  createChangeRequest: (payload) => api.post('/api/saas/self-service/subscription/change-request', payload),
  listChangeRequests: () => api.get('/api/saas/self-service/subscription/change-requests'),
  listPaymentHistory: () => api.get('/api/saas/self-service/subscription/payments/history'),
  getChangeRequestPayment: (requestId) =>
    api.get(`/api/saas/self-service/subscription/change-requests/${requestId}/payment`),
  refreshChangeRequestPayment: (requestId) =>
    api.post(`/api/saas/self-service/subscription/change-requests/${requestId}/payment/refresh`, {}),
  revokeChangeRequest: (requestId) =>
    api.post(`/api/saas/self-service/subscription/change-requests/${requestId}/revoke`, {}),
};

export const saasOnboardingService = {
  listSignupPlans: () => api.get('/api/onboarding/signup-plans'),
  signup: (payload) => api.post('/api/onboarding/signup', payload),
};

export const tenantContextService = {
  searchTenants: (q, limit = 10) =>
    api.get('/api/tenant-context/search', {
      params: {
        q: String(q || '').trim(),
        limit,
      },
    }),
  getTenantContext: (tenantSlug) =>
    api.get(`/api/tenant-context/${encodeURIComponent(String(tenantSlug || '').trim())}`),
  signupInTenantContext: (tenantSlug, payload) =>
    api.post(`/api/tenant-context/${encodeURIComponent(String(tenantSlug || '').trim())}/signup`, payload),
};

// Dashboard Services
export const dashboardService = {
  getExecutivo: (params) => api.get('/api/dashboard/executivo', { params }),
  getFinanceiro: (params) => api.get('/api/dashboard/financeiro', { params }),
  getParcelasDashboard: (params) => api.get('/api/dashboard/parcelas', { params }),
  getReceitaPorModalidade: (params) => api.get('/api/dashboard/receita-por-modalidade', { params }),
  getLucroPorTurma: (params) => api.get('/api/dashboard/lucro-por-turma', { params }),
  getLucroPorModalidade: (params) => api.get('/api/dashboard/lucro-por-modalidade', { params }),
  getForecastParcelas: (params) => api.get('/api/dashboard/forecast-parcelas', { params }),
  getTopEscolas: () => api.get('/api/dashboard/top-escolas'),
  getOperacional: (params) => api.get('/api/dashboard/operacional', { params }),
  getComercial: (params) => api.get('/api/dashboard/comercial', { params }),
  getRH: (params) => api.get('/api/dashboard/rh', { params }),
  getFinanceiroDetalhado: (params) => api.get('/api/dashboard/financeiro-detalhado', { params }),
  getUnidades: (params) => api.get('/api/dashboard/unidades', { params }),
};

// Portal Services
export const portalService = {
  getUnidades: (params) => api.get('/api/portal/unidades', { params }),
  getAlunos: (params) => api.get('/api/portal/alunos', { params }),
  createAluno: (payload) => api.post('/api/portal/alunos', payload),
  updateAluno: (alunoId, payload) => api.put(`/api/portal/alunos/${alunoId}`, payload),
  getMatriculas: (params) => api.get('/api/portal/matriculas', { params }),
  getTurmas: (params) => api.get('/api/portal/turmas', { params }),
  getParcelas: () => api.get('/api/portal/parcelas'),
  getFaturas: () => api.get('/api/portal/faturas'),
  createMatricula: (data) => api.post('/api/portal/matriculas', data),
  getMeuCadastroResponsavel: () => api.get('/api/responsaveis/meu-cadastro'),
  updateMeuCadastroResponsavel: (data) => api.put('/api/responsaveis/meu-cadastro', data),
  listarFrequencias: ({ alunoId, matriculaId, dataInicio, dataFim } = {}) => api.get('/api/portal/frequencia', {
    params: {
      aluno_id: alunoId ?? undefined,
      matricula_id: matriculaId ?? undefined,
      data_inicio: dataInicio ?? undefined,
      data_fim: dataFim ?? undefined,
    },
  }),
  obterDetalheFrequencia: ({ matriculaId, dataInicio, dataFim } = {}) => api.get(
    `/api/portal/frequencia/${matriculaId}/detalhe`,
    {
      params: {
        data_inicio: dataInicio ?? undefined,
        data_fim: dataFim ?? undefined,
      },
    }
  ),
};
export default api;
